import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Home,
  Store,
  DollarSign,
  Receipt,
  TrendingUp,
  FileText,
  PieChart,
  CreditCard,
  History,
  Crown,
  Users as UsersIcon,
  Target,
  UserPlus,
  FileCheck,
  Search as SearchIcon,
  ChevronDown,
  ChevronRight,
  Menu as MenuIcon,
  X,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  AlertCircle,
  AlertTriangle,
  Building2,
  Database,
  Wallet,
  LogOut,
  ClipboardCheck,
} from "lucide-react";

const navigationConfig = {
  operations: {
    title: "Daily Operations",
    icon: Store,
    roles: ["admin", "superuser", "manager", "restaurant_manager"],
    items: [
      { id: "restaurants", label: "Restaurants", icon: Store, roles: ["admin", "superuser", "manager"] },
      { id: "revenueentry", label: "Revenue Entry", icon: DollarSign, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
      { id: "expenseentry", label: "Expense Entry", icon: Receipt, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
      { id: "review_audit", label: "Review & Audit", icon: ClipboardCheck, roles: ["admin", "superuser", "manager"] },
    ]
  },
  targets: {
    title: "Targets",
    icon: Target,
    roles: ["admin", "superuser", "manager", "restaurant_manager"],
    items: [
      { id: "targets", label: "Restaurant Targets", icon: Target, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
      { id: "targetanalytics", label: "Target Analytics", icon: PieChart, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
    ]
  },
  analytics: {
    title: "Financial Reports",
    icon: TrendingUp,
    roles: ["admin", "superuser", "manager", "restaurant_manager"],
    items: [
      { id: "revenuehistory", label: "Revenue History", icon: FileText, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
      { id: "reports", label: "Reports", icon: PieChart, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
      { id: "profitloss", label: "P&L Report", icon: TrendingUp, roles: ["admin", "superuser", "manager"] },
      { id: "pettycashsummary", label: "Expense Summary", icon: CreditCard, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
    ]
  },
  finance: {
    title: "Cash & Royalty",
    icon: CreditCard,
    roles: ["admin", "superuser", "manager", "restaurant_manager"],
    items: [
      { id: "cashhandover", label: "Cash Handover", icon: DollarSign, roles: ["admin", "superuser", "manager"] },
      { id: "restaurantcash", label: "Restaurant Cash", icon: Wallet, roles: ["admin", "superuser", "manager", "restaurant_manager"] },
      { id: "cashhistory", label: "Cash History", icon: History, roles: ["admin", "superuser", "manager"] },
      { id: "cashreconciliation", label: "Cash Reconciliation", icon: PieChart, roles: ["admin", "superuser", "manager"] },
      { id: "royaltyentry", label: "Royalty Entry", icon: Crown, roles: ["admin", "superuser", "manager"] },
      { id: "royaltysummary", label: "Royalty Summary", icon: PieChart, roles: ["admin", "superuser", "manager"] },
      { id: "platformdeductions", label: "Platform Fees", icon: Receipt, roles: ["admin", "superuser", "manager"] },
      { id: "headofficeexpenses", label: "HO Expenses", icon: Building2, roles: ["admin", "superuser", "manager"] },
    ]
  },
  management: {
    title: "Team & Documents",
    icon: UsersIcon,
    roles: ["admin", "superuser", "manager"],
    items: [
      { id: "users", label: "Users", icon: UsersIcon, roles: ["admin", "superuser", "manager"] },
      { id: "employees", label: "Employees", icon: UserPlus, roles: ["admin", "superuser", "manager"] },
      { id: "documents", label: "Documents", icon: FileCheck, roles: ["admin", "superuser", "manager"] },
      { id: "credentials", label: "Credentials", icon: FileCheck, roles: ["admin", "superuser", "manager"] },
      { id: "datamanager", label: "Data Manager", icon: Database, roles: ["admin", "superuser"] },
    ]
  },
  support: {
    title: "Support",
    icon: AlertCircle,
    roles: ["admin", "superuser", "manager"],
    items: [
      { id: "anomalies", label: "Anomaly Monitor", icon: AlertTriangle, roles: ["admin", "superuser", "manager"] },
      { id: "issues", label: "Issue Tracker", icon: AlertCircle, roles: ["admin", "superuser", "manager"] },
    ]
  },
  system: {
    title: "Settings",
    icon: FileCheck,
    roles: ["admin", "superuser", "manager"],
    items: [
      { id: "categories", label: "Revenue Categories", icon: TrendingUp, roles: ["admin", "superuser", "manager"] },
      { id: "storemanagers", label: "Store Managers", icon: UsersIcon, roles: ["admin", "superuser", "manager"] },
      { id: "auditlogs", label: "Audit Logs", icon: History, roles: ["admin", "superuser"] },
    ]
  }
};

const SideNavigation = ({ activeTab, setActiveTab, user, onUserUpdate, onLogout, isMobileOpen, setIsMobileOpen, isCollapsed, setIsCollapsed }) => {
  const [expandedGroups, setExpandedGroups] = useState({
    operations: true,
    targets: false,
    analytics: false,
    finance: false,
    management: false,
    support: false,
    system: false
  });
  const [searchQuery, setSearchQuery] = useState("");

  const displayName = user?.name || user?.username || "";
  const initials = displayName.charAt(0).toUpperCase();

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    // Close mobile menu after navigation
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
  };

  const hasAccess = (roles) => {
    return roles.includes(user?.role);
  };

  // Filter navigation based on role
  const getFilteredNavigation = () => {
    const filtered = {};
    Object.keys(navigationConfig).forEach(groupKey => {
      const group = navigationConfig[groupKey];
      if (hasAccess(group.roles)) {
        const filteredItems = group.items.filter(item => hasAccess(item.roles));
        if (filteredItems.length > 0) {
          filtered[groupKey] = {
            ...group,
            items: filteredItems
          };
        }
      }
    });
    return filtered;
  };

  // Search functionality
  const getSearchResults = () => {
    if (!searchQuery.trim()) return [];
    
    const results = [];
    const filtered = getFilteredNavigation();
    
    Object.values(filtered).forEach(group => {
      group.items.forEach(item => {
        if (item.label.toLowerCase().includes(searchQuery.toLowerCase())) {
          results.push({
            ...item,
            groupTitle: group.title
          });
        }
      });
    });
    
    return results;
  };

  const filteredNavigation = getFilteredNavigation();
  const searchResults = getSearchResults();

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white z-50
          transition-all duration-300 ease-in-out
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
          ${isCollapsed ? 'w-20' : 'w-64'}
          flex flex-col
          overflow-hidden
        `}
      >
        {/* Header with Collapse Button */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
          {!isCollapsed && (
            <div className="flex items-center gap-2">
              <Store className="w-6 h-6 text-blue-400" />
              <span className="font-bold text-lg">N Square Cafe</span>
            </div>
          )}
          {isCollapsed && (
            <div className="w-full flex justify-center">
              <Store className="w-6 h-6 text-blue-400" />
            </div>
          )}
          
          {/* Desktop Collapse Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:flex text-white hover:bg-gray-700 p-1"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronsRight className="w-5 h-5" /> : <ChevronsLeft className="w-5 h-5" />}
          </Button>

          {/* Mobile Close Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsMobileOpen(false)}
            className="lg:hidden text-white hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Search Bar */}
        {!isCollapsed && (
          <div className="p-4 border-b border-gray-700 flex-shrink-0">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search menu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-gray-800 border-gray-600 text-white placeholder:text-gray-400 focus:border-blue-500"
              />
            </div>

            {/* Search Results */}
            {searchQuery && (
              <div className="mt-2 bg-gray-800 rounded-lg max-h-64 overflow-y-auto">
                {searchResults.length > 0 ? (
                  searchResults.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          handleNavClick(item.id);
                          setSearchQuery("");
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-700 flex items-center gap-2 text-sm"
                      >
                        <Icon className="w-4 h-4 text-blue-400" />
                        <div>
                          <div className="text-white">{item.label}</div>
                          <div className="text-xs text-gray-400">{item.groupTitle}</div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="px-3 py-2 text-sm text-gray-400">No results found</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Collapsed Search Icon */}
        {isCollapsed && (
          <div className="p-4 border-b border-gray-700 flex justify-center flex-shrink-0">
            <SearchIcon className="w-5 h-5 text-gray-400" />
          </div>
        )}

        {/* Navigation Groups - Scrollable */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
          {/* Dashboard (Hidden for restaurant_manager) */}
          {user?.role !== "restaurant_manager" && (
            <button
              onClick={() => handleNavClick("dashboard")}
              className={`
                w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                ${activeTab === "dashboard" ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}
                ${isCollapsed ? 'justify-center' : ''}
              `}
              title={isCollapsed ? "Dashboard" : ""}
            >
              <Home className="w-5 h-5 flex-shrink-0" />
              {!isCollapsed && <span>Dashboard</span>}
            </button>
          )}

          {/* Navigation Groups */}
          {Object.entries(filteredNavigation).map(([groupKey, group]) => {
            const GroupIcon = group.icon;
            const isExpanded = expandedGroups[groupKey];

            return (
              <div key={groupKey} className="space-y-1">
                {/* Group Header */}
                <button
                  onClick={() => !isCollapsed && toggleGroup(groupKey)}
                  className={`
                    w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-700 transition-colors
                    ${isCollapsed ? 'justify-center' : ''}
                  `}
                  title={isCollapsed ? group.title : ""}
                >
                  <div className="flex items-center gap-3">
                    <GroupIcon className="w-5 h-5 flex-shrink-0" />
                    {!isCollapsed && <span className="font-medium text-sm">{group.title}</span>}
                  </div>
                  {!isCollapsed && (
                    isExpanded ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />
                  )}
                </button>

                {/* Group Items */}
                {isExpanded && !isCollapsed && (
                  <div className="ml-4 space-y-1">
                    {group.items.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavClick(item.id)}
                          className={`
                            w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm
                            ${activeTab === item.id ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}
                          `}
                        >
                          <ItemIcon className="w-4 h-4 flex-shrink-0" />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Collapsed - Show items on hover */}
                {isCollapsed && (
                  <div className="relative group">
                    {/* Hover Popup */}
                    <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50">
                      <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-2 min-w-[200px]">
                        <div className="px-3 py-1 text-xs font-semibold text-gray-400 border-b border-gray-700 mb-1">
                          {group.title}
                        </div>
                        {group.items.map((item) => {
                          const ItemIcon = item.icon;
                          return (
                            <button
                              key={item.id}
                              onClick={() => handleNavClick(item.id)}
                              className={`
                                w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-700 text-sm
                                ${activeTab === item.id ? 'bg-blue-600 text-white' : 'text-gray-300'}
                              `}
                            >
                              <ItemIcon className="w-4 h-4 flex-shrink-0" />
                              <span>{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User Info - Fixed at Bottom */}
        {!isCollapsed && (
          <div className="p-3 border-t border-gray-700 flex-shrink-0 space-y-2">
            <div className="flex items-center gap-3 p-1">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold">{initials}</span>
              </div>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-medium truncate" data-testid="sidebar-display-name">{displayName}</div>
                <div className="text-xs text-gray-400 capitalize">{user?.role}</div>
              </div>
            </div>
            {onLogout && (
              <button onClick={onLogout} className="w-full flex items-center gap-2 p-2 rounded-lg text-xs text-gray-400 hover:text-red-400 hover:bg-gray-700/50 transition-colors" data-testid="logout-btn">
                <LogOut className="w-3.5 h-3.5" /> Logout
              </button>
            )}
          </div>
        )}

        {/* Collapsed User Avatar */}
        {isCollapsed && (
          <div className="p-3 border-t border-gray-700 flex flex-col items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center" title={displayName}>
              <span className="text-sm font-semibold">{initials}</span>
            </div>
            {onLogout && (
              <button onClick={onLogout} className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-gray-700/50 transition-colors" data-testid="logout-btn-collapsed">
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
};

export default SideNavigation;
